import { Component, DestroyRef, Inject, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import {
    AppNotificationService,
    AppNotificationType,
    ConfigService,
    EquipmentModel,
    NotificationModel,
    NotificationToModifyOtherTask,
    NotificationService,
    OtherTaskNotificationModel,
    OtherTasksService,
    NotificationToGetOtherTasks,
    ValueHelperService,
} from '@evorait/pace-core';
import { catchError, filter, finalize, take, tap } from 'rxjs/operators';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import moment from 'moment';
import { EMPTY, of, switchMap } from 'rxjs';
import { otherTaskToPreview, PaceNotificationPreviewInterface } from '@evorait/pace-components';
import { PaceLink } from '../../../../../../libs/pace-components/src/lib/utils/link.interface';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

@Component({
    selector: 'pace-equipment-other-tasks',
    templateUrl: './equipment-other-tasks.component.html',
    styleUrls: ['./equipment-other-tasks.component.scss'],
})
export class EquipmentOtherTasksComponent implements OnInit {
    @Input({ required: true }) public notification: NotificationModel;

    public equipmentNotifications: OtherTaskNotificationModel[] = [];
    public customerNotifications: OtherTaskNotificationModel[] = [];
    public description: string;
    public isLoading = true;
    public hasCustomerId: boolean;
    public showSuccessMessage: boolean;
    public isRequesting = false;
    public assignedTask: NotificationToModifyOtherTask | undefined;
    public requestError = false;
    public successBackLink: PaceLink;
    private mainEquipment: EquipmentModel | undefined;
    private notificationParamsToGetOtherTasks: NotificationToGetOtherTasks;
    private customerId: string;
    private readonly overlapUpcomingNotification: boolean;
    private pickedTasksFromIndexedDB: string[];

    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        public readonly modalInstance: BsModalRef,
        private readonly translate: TranslateService,
        private readonly configService: ConfigService,
        private readonly notificationService: NotificationService,
        private readonly appNotificationService: AppNotificationService,
        private readonly otherTasksService: OtherTasksService,
        private readonly valueHelperService: ValueHelperService,
        private readonly destroyRef: DestroyRef,
    ) {
        this.overlapUpcomingNotification = this.configService.getOtherTasksOverlapUpcomingNoti();
        this.successBackLink = {
            label: this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_OTHER_TASKS_SUCCESS_BACK'),
            icon: faArrowLeft,
            action: (): void => {
                this.modalInstance.hide();
            },
        };
    }

    public ngOnInit(): void {
        this.hasCustomerId = false;
        this.mainEquipment = this.notification.getMainEquipment();
        this.notificationParamsToGetOtherTasks = this.notification.getNotificationRequestParamsForGetOtherTasks();
        this.description = this.mainEquipment ? this.mainEquipment.equiShortDesc.value : '';

        this.otherTasksService.syncCompleted$
            .pipe(
                take(1),
                switchMap(() => this.otherTasksService.getAllPickedTasks()),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe((alreadyPickedTasks: string[]) => {
                this.pickedTasksFromIndexedDB = [...alreadyPickedTasks];
                this.getTasksByEquipment();
            });

        this.otherTasksService.triggerSyncConcurrent();
    }

    public getTypeLabel(task: NotificationToModifyOtherTask | undefined): string {
        return task ? this.valueHelperService.getNotificationTypeLabel(this.notification.notificationType) : '';
    }

    public convertToPreview(task: NotificationToModifyOtherTask): PaceNotificationPreviewInterface {
        return otherTaskToPreview(task);
    }

    private hasNotifications(notifications: OtherTaskNotificationModel[]): boolean {
        return Array.isArray(notifications) && notifications.length > 0;
    }

    public getTasksByEquipment(): void {
        if (!this.mainEquipment) return;
        this.isLoading = true;
        this.showSuccessMessage = false;
        this.otherTasksService
            .getOtherTasksByEquipment(this.notificationParamsToGetOtherTasks, this.mainEquipment.equnr.value)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((error) => {
                    this.log.error(
                        'EquipmentOtherTasksComponent: getTasksByEquipment: Error while fetching Other Tasks by equipment:',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>(
                            this.translate.instant(
                                'NOTIFICATION_DETAIL_EQUIPMENTS_OTHER_TASKS_GET_BY_EQUIPMENT_ERROR_TITLE',
                            )
                        ),
                        <string>(
                            this.translate.instant(
                                'NOTIFICATION_DETAIL_EQUIPMENTS_OTHER_TASKS_GET_BY_EQUIPMENT_ERROR_BODY',
                            )
                        ),
                    );
                    this.requestError = true;
                    return of<OtherTaskNotificationModel[]>([]);
                }),
                filter((notifications) => this.hasNotifications(notifications)),
                tap((notifications) => {
                    this.equipmentNotifications = this.checkAndHideGetTaskButton(notifications);
                    this.setCustomerId(notifications[0].customer);
                }),
                finalize(() => (this.isLoading = false)),
            )
            .subscribe();
    }

    public getTasksByCustomer(): void {
        this.isLoading = true;
        this.showSuccessMessage = false;
        this.otherTasksService
            .getOtherTasksByCustomer(this.notificationParamsToGetOtherTasks, this.customerId)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((error) => {
                    this.log.error(
                        'EquipmentOtherTasksComponent: getTasksByCustomer: Error while fetching Other Tasks by CustomerId:',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>(
                            this.translate.instant(
                                'NOTIFICATION_DETAIL_EQUIPMENTS_OTHER_TASKS_GET_BY_CUSTOMER_ERROR_TITLE',
                            )
                        ),
                        <string>(
                            this.translate.instant(
                                'NOTIFICATION_DETAIL_EQUIPMENTS_OTHER_TASKS_GET_BY_CUSTOMER_ERROR_TITLE',
                            )
                        ),
                    );
                    this.requestError = true;
                    return of<OtherTaskNotificationModel[]>([]);
                }),
                filter((notifications) => this.hasNotifications(notifications)),
                tap((notifications) => {
                    this.customerNotifications = this.checkAndHideGetTaskButton(notifications);
                }),
                finalize(() => (this.isLoading = false)),
            )
            .subscribe();
    }

    public getTask(taskData: NotificationToModifyOtherTask): void {
        this.isRequesting = true;
        this.requestError = false;
        this.showSuccessMessage = false;
        this.log.debug(
            `EquipmentOtherTasksComponent: getTask - params sending in POST request: ${JSON.stringify(taskData)}`,
        );
        this.otherTasksService
            .getTask(taskData)
            .pipe(
                takeUntilDestroyed(this.destroyRef),
                catchError((error) => {
                    this.log.error(
                        'EquipmentOtherTaskComponent: getTask: Error while self assigning a task from other tasks: ',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    );
                    this.appNotificationService.setAppNotification(
                        AppNotificationType.ERROR,
                        <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_OTHER_TASKS_GET_ERROR_TITLE'),
                        <string>this.translate.instant('NOTIFICATION_DETAIL_EQUIPMENTS_OTHER_TASKS_GET_ERROR_BODY'),
                    );
                    this.requestError = true;
                    return EMPTY;
                }),
                tap((assignedTask) => {
                    this.showSuccessMessage = true;
                    this.assignedTask = assignedTask;
                }),
                switchMap(() =>
                    this.otherTasksService.saveTaskOriginalPlannedDate(taskData).pipe(
                        catchError((error) => {
                            this.log.error(
                                'EquipmentOtherTaskComponent: saveTaskOriginalPlannedDate: Error while saving original planned date of picked task: ',
                                JSON.stringify(error, Object.getOwnPropertyNames(error)),
                            );
                            return EMPTY;
                        }),
                    ),
                ),
                finalize(() => {
                    this.isRequesting = false;
                }),
            )
            .subscribe();
    }

    private setCustomerId(customerId: string): void {
        this.customerId = customerId?.trim() ?? '';
        this.hasCustomerId = this.customerId.length > 0;
        if (this.hasCustomerId) {
            this.getTasksByCustomer();
        }
    }

    private getTaskDurationInSeconds(taskDuration: string): number {
        const parsedTaskDuration = parseFloat(taskDuration || '0');
        return isNaN(parsedTaskDuration) ? 0 : parsedTaskDuration;
    }

    private checkAndHideGetTaskButton(notifications: OtherTaskNotificationModel[]): OtherTaskNotificationModel[] {
        const nextNotification: NotificationModel | undefined = this.notificationService.getNextUpcomingNotification(
            this.notification,
        );
        if (!nextNotification) return notifications;

        const now = moment();
        const timeTillNextNotificationStartsInSeconds = nextNotification.plannedStart.diff(now, 'seconds');
        const timeTillCurrentNotificationEndsInSeconds = this.notification.plannedEnd.diff(now, 'seconds');

        return notifications.map((notification) => {
            if (notification.canGetTask) {
                const notificationTaskId = `${notification.notificationNo}::${notification.taskNo}`;

                const taskDurationInSeconds = this.getTaskDurationInSeconds(notification.duration);
                // check for picked tasks in indexed DB because gateway takes time to update
                if (this.pickedTasksFromIndexedDB?.includes(notificationTaskId)) {
                    this.log.debug(
                        `EquipmentOtherTasksComponent: checkAndHideGetTaskButton – Task[${notification.notificationNo}(${notification.taskNo})] already exists in IndexedDB and cannot be picked again.`,
                    );
                    notification.canGetTask = false;
                } else if (taskDurationInSeconds > timeTillNextNotificationStartsInSeconds) {
                    this.log.debug(
                        `EquipmentOtherTasksComponent: checkAndHideGetTaskButton - Duration of task[${notification.notificationNo}(${notification.taskNo})] i.e. ${taskDurationInSeconds}s exceeds the time left to next notification. Next Notification[${nextNotification.identifier.qmNum}(${nextNotification.identifier.qmNum})] starts on ${nextNotification.plannedStart}`,
                    );
                    notification.canGetTask = false;
                } else if (
                    taskDurationInSeconds > timeTillCurrentNotificationEndsInSeconds &&
                    !this.overlapUpcomingNotification
                ) {
                    this.log.debug(
                        `EquipmentOtherTasksComponent: checkAndHideGetTaskButton - Duration of task[${notification.notificationNo}(${notification.taskNo})] i.e. ${taskDurationInSeconds}s exceeds the time left to finish current notification[${this.notification.identifier.qmNum}(${this.notification.identifier.qmNum})] that is ending on ${this.notification.plannedEnd} and overlapping isn't allowed`,
                    );
                    notification.canGetTask = false;
                }
            }
            return notification;
        });
    }
}
